import * as React from "react";

type OrderBy = "ASC" | "DESC";
const OrderBy = {
    ASC: "ASC" as OrderBy,
    DESC: "DESC" as OrderBy,
};

interface BaseColumn<Row, Data, SortableData, RawData> {
    name: string;
    label: string;
    headerCellClassName?: string;
    getData: (row: Row, rowNumber: number) => Data;
    getSortData?: (row: Row, rowNumber: number) => SortableData;
    getRawData?: (row: Row, rowNumber: number) => RawData;
    getTextData?: (row: Row, rowNumber: number) => string;
    getCopyableData?: (row: Row, rowNumber: number) => string | number;
    getCellErrorId?: (row: Row, rowNumber: number) => string;
    headerWidget?: React.ReactNode;
    // To determine whether a cell is editable, the table checks the presence of
    // getEditor without calling it to avoid too much computation.
    // However getEditor may return null for non editable cells. In that case
    // you should implement isEditable.
    isEditable?: (rowNumber: number) => boolean; // Default (getEditor ? true : false)
    getEditor?: (rowNumber: number, data: Data) => Editor<Data> | null;
    maxEditorLength?: number;
    cellClassName?: string;
    onClick?: (row: Row, rowNumber: number) => void;
}

interface FixedWidthColumn<Row, Data, SortableData, RawData> extends BaseColumn<
    Row,
    Data,
    SortableData,
    RawData
> {
    fixedWidth?: number;
}

interface MinWidthColumn<Row, Data, SortableData, RawData> extends BaseColumn<
    Row,
    Data,
    SortableData,
    RawData
> {
    minWidth?: number;
}

type Column<Row, Data, SortableData, RawData> =
    | FixedWidthColumn<Row, Data, SortableData, RawData>
    | MinWidthColumn<Row, Data, SortableData, RawData>;

type Editor<Data> =
    | React.FC<EditorProps<Data>>
    | React.ComponentClass<EditorProps<Data>>;

interface EditorProps<Data> {
    data: Data | string;
    maxLength?: number;
    onBlurableRef: (blurable: Blurable) => void;
    onBlur: (newData: Data | null) => void;
}

interface Blurable {
    blur: () => void;
}

type CellKeyboardEventType =
    | "MOVEMENT"
    | "SELECTION_MOVEMENT"
    | "NEXT_CELL"
    | "PREVIOUS_CELL"
    | "END_EDITION_AND_NEXT_CELL"
    | "END_EDITION_AND_PREVIOUS_CELL"
    | "COPY"
    | "SELECT_ALL"
    | "START_EDITION"
    | "END_EDITION"
    | "DELETION";
const CellKeyboardEventType = {
    MOVEMENT: "MOVEMENT" as CellKeyboardEventType,
    SELECTION_MOVEMENT: "SELECTION_MOVEMENT" as CellKeyboardEventType,
    NEXT_CELL: "NEXT_CELL" as CellKeyboardEventType,
    PREVIOUS_CELL: "PREVIOUS_CELL" as CellKeyboardEventType,
    END_EDITION_AND_NEXT_CELL:
        "END_EDITION_AND_NEXT_CELL" as CellKeyboardEventType,
    END_EDITION_AND_PREVIOUS_CELL:
        "END_EDITION_AND_PREVIOUS_CELL" as CellKeyboardEventType,
    COPY: "COPY" as CellKeyboardEventType,
    SELECT_ALL: "SELECT_ALL" as CellKeyboardEventType,
    START_EDITION: "START_EDITION" as CellKeyboardEventType,
    END_EDITION: "END_EDITION" as CellKeyboardEventType,
    DELETION: "DELETION" as CellKeyboardEventType,
};

interface CellKeyboardEvent {
    type: CellKeyboardEventType;
    key?: string;
}

interface BaseCell<ROW> {
    row: ROW;
    columnName: string;
}

interface FocusedCell<ROW> extends BaseCell<ROW> {
    type: "FOCUS";
}

interface EditedCell<ROW> extends BaseCell<ROW> {
    type: "EDIT";
}

interface SelectedCellArea<ROW> extends BaseCell<ROW> {
    type: "SELECT";
    anchorRow: ROW;
    anchorColumnName: string;
}

type SelectionDescriptor<ROW> =
    | FocusedCell<ROW>
    | EditedCell<ROW>
    | SelectedCellArea<ROW>;

interface RawRow {
    readonly rawRowNumber: number;
}

type RawSelectionDescriptor = SelectionDescriptor<RawRow>;

interface SortedRow {
    readonly sortedRowNumber: number;
}

type SortedSelectionDescriptor = SelectionDescriptor<SortedRow>;

export { OrderBy, CellKeyboardEventType };
export type {
    Column,
    FixedWidthColumn,
    MinWidthColumn,
    Editor,
    EditorProps,
    Blurable,
    CellKeyboardEvent,
    FocusedCell,
    EditedCell,
    SelectedCellArea,
    SelectionDescriptor,
    RawRow,
    RawSelectionDescriptor,
    SortedRow,
    SortedSelectionDescriptor,
};
